import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Menu, X, User, LogOut, LayoutDashboard, Gift, MessageSquare, Bell, Trophy, Wand2, Share2, ShoppingBag } from 'lucide-react';
import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Header = () => {
  const { user, isAuthenticated, logout, token } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isAuthenticated && token) {
      fetchUnreadCount();
    }
  }, [isAuthenticated, token]);

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/messages/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(response.data.unread);
    } catch (error) {
      // Silently fail
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <header className={`fixed w-full z-50 transition-all duration-300 ${
      scrolled ? 'bg-neutral-900/95 backdrop-blur-xl border-b border-neutral-800 shadow-lg' : 'bg-black/20 backdrop-blur-sm'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo">
            <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit' }}>
              <span 
                className="text-white"
                style={{ 
                  textShadow: '0 1px 3px rgba(0,0,0,0.5), 0 0 20px rgba(0,0,0,0.3)'
                }}
              >
                Spin
              </span>
              <span className="text-[#FF3B30]" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>ser</span>
            </h1>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <Link 
              to="/giveaways" 
              className="font-medium transition-colors text-white/80 hover:text-white"
              data-testid="nav-giveaways"
            >
              Giveaways
            </Link>
            <Link 
              to="/spotlights" 
              className="font-medium transition-colors flex items-center gap-1 text-white/80 hover:text-white"
              data-testid="nav-spotlights"
            >
              <Trophy className="w-4 h-4" />
              Winners
            </Link>
            <Link 
              to="/nominate" 
              className="font-medium transition-colors text-white/80 hover:text-white"
              data-testid="nav-nominate"
            >
              Nominate
            </Link>
            <Link 
              to="/sponsor-signup" 
              className="font-medium transition-colors text-white/80 hover:text-white whitespace-nowrap"
              data-testid="nav-sponsors"
            >
              For Sponsors
            </Link>
            <Link 
              to="/leaderboard" 
              className="font-medium transition-colors text-white/80 hover:text-white"
              data-testid="nav-leaderboard"
            >
              Leaderboard
            </Link>
            <Link 
              to="/envision" 
              className="font-medium transition-colors flex items-center gap-1 text-white/80 hover:text-white"
              data-testid="nav-envision"
            >
              <Wand2 className="w-4 h-4" />
              Envision
            </Link>
            <Link 
              to="/merch" 
              className="font-medium transition-colors flex items-center gap-1 text-white/80 hover:text-white"
              data-testid="nav-merch"
            >
              <ShoppingBag className="w-4 h-4" />
              Merch
            </Link>
            <Link 
              to="/refer" 
              className="font-medium transition-colors flex items-center gap-1.5 bg-gradient-to-r from-[#FF3B30] to-[#FF9500] text-white px-3 py-1.5 rounded-full hover:opacity-90"
              data-testid="nav-refer"
            >
              <Share2 className="w-4 h-4" />
              Refer & Earn
            </Link>
          </nav>

          {/* Auth Buttons */}
          <div className="hidden md:flex items-center gap-4">
            {isAuthenticated ? (
              <>
                {/* Messages */}
                <Link to="/dashboard/messages" className="relative">
                  <Button variant="ghost" size="icon" className="text-white/80 hover:text-white hover:bg-white/10">
                    <MessageSquare className="w-5 h-5" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#FF3B30] text-white text-xs rounded-full flex items-center justify-center">
                        {unreadCount}
                      </span>
                    )}
                  </Button>
                </Link>

                {/* User Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-2 text-white/80 hover:text-white hover:bg-white/10" data-testid="user-menu">
                      <div className="w-8 h-8 rounded-full bg-[#FF3B30] flex items-center justify-center text-white font-medium">
                        {user?.name?.charAt(0).toUpperCase() || 'U'}
                      </div>
                      <span className="hidden lg:inline">{user?.name?.split(' ')[0]}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-3 py-2">
                      <p className="font-medium text-neutral-900">{user?.name}</p>
                      <p className="text-sm text-neutral-500">{user?.email}</p>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" className="flex items-center gap-2 cursor-pointer">
                        <LayoutDashboard className="w-4 h-4" />
                        Dashboard
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard/entries" className="flex items-center gap-2 cursor-pointer">
                        <Gift className="w-4 h-4" />
                        My Entries
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard/profile" className="flex items-center gap-2 cursor-pointer">
                        <User className="w-4 h-4" />
                        Profile
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer" data-testid="logout-btn">
                      <LogOut className="w-4 h-4 mr-2" />
                      Log out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="text-white/80 hover:text-white hover:bg-white/10" data-testid="login-btn">
                    Log in
                  </Button>
                </Link>
                <Link to="/login">
                  <Button className="bg-[#FF3B30] hover:bg-[#DC2626] text-white rounded-xl" data-testid="get-started-btn">
                    Get Started
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-btn"
          >
            {isOpen ? (
              <X className="w-6 h-6 text-white" />
            ) : (
              <Menu className="w-6 h-6 text-white" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden bg-white border-t border-neutral-200">
          <div className="px-4 py-4 space-y-3">
            <Link to="/giveaways" className="block py-2 text-neutral-600 font-medium" onClick={() => setIsOpen(false)}>
              Giveaways
            </Link>
            <Link to="/spotlights" className="block py-2 text-neutral-600 font-medium flex items-center gap-2" onClick={() => setIsOpen(false)}>
              <Trophy className="w-4 h-4" />
              Winners
            </Link>
            <Link to="/nominate" className="block py-2 text-neutral-600 font-medium" onClick={() => setIsOpen(false)}>
              Nominate
            </Link>
            <Link to="/sponsor-signup" className="block py-2 text-neutral-600 font-medium" onClick={() => setIsOpen(false)}>
              For Sponsors
            </Link>
            <Link to="/leaderboard" className="block py-2 text-neutral-600 font-medium" onClick={() => setIsOpen(false)}>
              Leaderboard
            </Link>
            <Link to="/envision" className="block py-2 text-neutral-600 font-medium flex items-center gap-2" onClick={() => setIsOpen(false)}>
              <Wand2 className="w-4 h-4" />
              Envision
            </Link>
            <Link to="/merch" data-testid="mobile-nav-merch" className="block py-2 text-neutral-600 font-medium flex items-center gap-2" onClick={() => setIsOpen(false)}>
              <ShoppingBag className="w-4 h-4" />
              Merch
            </Link>
            <Link to="/refer" className="block py-2 font-medium flex items-center gap-2" onClick={() => setIsOpen(false)}>
              <div className="flex items-center gap-2 bg-gradient-to-r from-[#FF3B30] to-[#FF9500] text-white px-3 py-1.5 rounded-full">
                <Share2 className="w-4 h-4" />
                Refer & Earn
              </div>
            </Link>
            
            <div className="pt-4 border-t border-neutral-200">
              {isAuthenticated ? (
                <>
                  <Link to="/dashboard" className="block py-2 text-neutral-600 font-medium" onClick={() => setIsOpen(false)}>
                    Dashboard
                  </Link>
                  <button onClick={handleLogout} className="block py-2 text-red-600 font-medium">
                    Log out
                  </button>
                </>
              ) : (
                <Link to="/login" onClick={() => setIsOpen(false)}>
                  <Button className="w-full bg-[#FF3B30] hover:bg-[#DC2626] text-white rounded-xl">
                    Get Started
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
